/**
 * Interview Sections Validation Helpers
 *
 * Helper functions for complex cross-section validations.
 * These are used by the enhanced validation schema to provide detailed error messages.
 *
 * These functions accept both old section types (content, confirmation, decision, statement, form)
 * and new section types (explore, gate, split, message, collect).
 */
/**
 * Check if all section references (next_section, choice targets) point to valid section IDs
 */
export function validateSectionReferences(sections) {
    const errors = [];
    const sectionIds = new Set(sections.map((s) => s.id));
    for (const section of sections) {
        // Check next_section reference (for content, statement, form sections)
        if ("next_section" in section &&
            section.next_section != null &&
            section.next_section !== "") {
            if (!sectionIds.has(section.next_section)) {
                errors.push(`Section "${section.id}" is connected to "${section.next_section}" which does not exist. Check the connection or create the missing section.`);
            }
        }
        // Check choice targets (for confirmation and decision sections)
        if ("choices" in section && section.choices) {
            for (const choice of section.choices) {
                if (!sectionIds.has(choice.target)) {
                    errors.push(`Section "${section.id}": Choice "${choice.name}" is connected to "${choice.target}" which does not exist. Check the connection or create the missing section.`);
                }
            }
        }
        // Check deprecated yes_path/no_path (backward compatibility check)
        if ("yes_path" in section && section.yes_path && section.yes_path !== "") {
            if (!sectionIds.has(section.yes_path)) {
                errors.push(`Section "${section.id}": yes_path "${section.yes_path}" does not exist (note: yes_path is deprecated, use choices instead)`);
            }
        }
        if ("no_path" in section && section.no_path && section.no_path !== "") {
            if (!sectionIds.has(section.no_path)) {
                errors.push(`Section "${section.id}": no_path "${section.no_path}" does not exist (note: no_path is deprecated, use choices instead)`);
            }
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
}
/**
 * Find sections that are not reachable from the first section
 * Returns section IDs that are orphaned (warning level)
 */
export function findOrphanedSections(sections) {
    if (sections.length === 0) {
        return { orphanedIds: [], warnings: [] };
    }
    const reachable = new Set();
    const firstSection = sections[0];
    if (!firstSection) {
        return { orphanedIds: [], warnings: [] };
    }
    const toVisit = [firstSection.id];
    // BFS to find all reachable sections
    while (toVisit.length > 0) {
        const currentId = toVisit.shift();
        if (!currentId || reachable.has(currentId))
            continue;
        reachable.add(currentId);
        const section = sections.find((s) => s.id === currentId);
        if (!section)
            continue;
        // Add next_section
        if ("next_section" in section && section.next_section) {
            toVisit.push(section.next_section);
        }
        // Add choice targets
        if ("choices" in section && section.choices) {
            for (const choice of section.choices) {
                toVisit.push(choice.target);
            }
        }
        // Add deprecated yes_path/no_path
        if ("yes_path" in section && section.yes_path && section.yes_path !== "") {
            toVisit.push(section.yes_path);
        }
        if ("no_path" in section && section.no_path && section.no_path !== "") {
            toVisit.push(section.no_path);
        }
    }
    const orphanedIds = sections
        .slice(1) // Skip first section (entry point)
        .filter((s) => !reachable.has(s.id))
        .map((s) => s.id);
    const warnings = orphanedIds.map((id) => `Section "${id}" is not reachable from any other section. Consider removing it or linking it to the interview flow.`);
    return { orphanedIds, warnings };
}
/**
 * Detect potential circular references (infinite loops)
 * Returns warnings for sections that may loop indefinitely
 */
export function detectCircularReferences(sections) {
    const warnings = [];
    const circularPaths = [];
    // For each section, do a DFS and track the path
    for (const startSection of sections) {
        const visited = new Set();
        const path = [];
        function dfs(sectionId) {
            if (path.includes(sectionId)) {
                // Found a cycle
                const cycleStart = path.indexOf(sectionId);
                const cycle = [...path.slice(cycleStart), sectionId];
                circularPaths.push(cycle);
                warnings.push(`Potential infinite loop detected: ${cycle.join(" → ")}`);
                return true;
            }
            if (visited.has(sectionId)) {
                return false;
            }
            visited.add(sectionId);
            path.push(sectionId);
            const section = sections.find((s) => s.id === sectionId);
            if (!section) {
                path.pop();
                return false;
            }
            // Check next_section
            if ("next_section" in section && section.next_section) {
                if (dfs(section.next_section)) {
                    path.pop();
                    return true;
                }
            }
            // Check choice targets
            if ("choices" in section && section.choices) {
                for (const choice of section.choices) {
                    if (dfs(choice.target)) {
                        path.pop();
                        return true;
                    }
                }
            }
            path.pop();
            return false;
        }
        dfs(startSection.id);
    }
    return { circularPaths, warnings };
}
/**
 * Validate that choice targets exist in the section list
 */
export function validateChoiceTargets(section, allSectionIds) {
    const errors = [];
    if ("choices" in section && section.choices) {
        for (const choice of section.choices) {
            if (!allSectionIds.has(choice.target)) {
                errors.push(`Choice "${choice.name}" is connected to "${choice.target}" which does not exist. Check the connection or create the missing section.`);
            }
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
}
/**
 * Validate that slot names are unique within a section
 */
export function validateSlotUniqueness(slots, sectionId) {
    const errors = [];
    const names = new Set();
    for (const slot of slots) {
        if (names.has(slot.name)) {
            errors.push(`Section "${sectionId}": Duplicate slot name "${slot.name}"`);
        }
        names.add(slot.name);
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
}
/**
 * Check for deprecated fields and provide migration hints
 */
export function validateDeprecatedFields(section) {
    const errors = [];
    // Check priority (deprecated for all sections)
    if ("priority" in section &&
        section.priority !== null &&
        section.priority !== undefined) {
        errors.push(`Section "${section.id}": Field "priority" is deprecated and should be removed or set to null`);
    }
    // Check quality_threshold (deprecated for content/explore sections)
    if ((section.section_type === "content" ||
        section.section_type === "explore") &&
        "quality_threshold" in section &&
        section.quality_threshold !== null &&
        section.quality_threshold !== undefined) {
        errors.push(`Section "${section.id}": Field "quality_threshold" is deprecated and should be removed or set to null`);
    }
    // Check yes_path/no_path (deprecated for confirmation/gate sections)
    if (section.section_type === "confirmation" ||
        section.section_type === "gate") {
        if ("yes_path" in section && section.yes_path && section.yes_path !== "") {
            errors.push(`Section "${section.id}": Field "yes_path" is deprecated. Use "choices" array instead with a choice named "yes"`);
        }
        if ("no_path" in section && section.no_path && section.no_path !== "") {
            errors.push(`Section "${section.id}": Field "no_path" is deprecated. Use "choices" array instead with a choice named "no"`);
        }
    }
    return {
        hasDeprecated: errors.length > 0,
        errors,
    };
}
/**
 * Check if interview has at least one terminal section
 */
export function hasTerminalSection(sections) {
    return sections.some((section) => {
        if ("next_section" in section) {
            return section.next_section === null;
        }
        return false;
    });
}
